import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  DebtClass,
  isAnnounced,
  isFundRaising,
  TransactionType,
  translateProjectStatus,
} from '../../../../api-client';
import apiClientConfig from '../../../../configs/app';
import { createProjectProvider } from '../../../../di';
import { formatDate } from '../../../../formatters';
import { useErrorCallback } from '../../../../hooks';
import { ymAsync } from '../../../../utils/functions/ymWrapper';
import { Button, Tooltip } from '../../../common';
import { debtDescriptionOfProjectCategory, valuesOfProjectCategory } from '../../../layout';
import { ProjectCategory } from '../../../project';
import { DetailedProjectContext } from '../DetailedProjectContext/DetailedProjectContext';
import ProjectMainStats from '../ProjectMainStats';
import styles from './ProjectMain.module.scss';

const ProjectMain = () => {
  const project = useContext(DetailedProjectContext).project;
  const handleError = useErrorCallback();
  const [presentationLoading, setPresentationLoading] = useState(false);

  const downloadPresentation = async () => {
    if (presentationLoading) return;
    setPresentationLoading(true);
    try {
      ymAsync('download-presentation-project-card');
      const provider = await createProjectProvider();
      const { url } = await provider.getPresentation(project.uuid);
      window.location.assign(apiClientConfig.apiBaseUrl + url);
    } catch (error) {
      if (error instanceof Error) await handleError(error);
    } finally {
      setPresentationLoading(false);
    }
  };
  let investmentFeePercent = 0;
  let dividendInterestFeePercent = 0;

  project.feeSettings.map((setting) => {
    if (setting.transactionType === TransactionType.INVESTMENT_FEE && setting.percent)
      return (investmentFeePercent = setting.percent);
    if (setting.transactionType === TransactionType.DIVIDEND_INTEREST_FEE && setting.percent)
      return (dividendInterestFeePercent = setting.percent);
    return null;
  });

  const renderPaymentBases = useMemo(() => {
    const paymentBases = project.paymentBases;
    if (paymentBases.length === 0) {
      return <div>Основания будут добавлены в процессе наполнения проекта.</div>;
    }
    return paymentBases.map((paymentBase) => {
      return (
        <div key={paymentBase.heading} className={styles.detailedProjectAdditional__item}>
          <div className={styles.detailedProjectAdditional__heading}>{paymentBase.heading}</div>
          <div className={styles.detailedProjectAdditional__content}>{paymentBase.content}</div>
        </div>
      );
    });
  }, [project]);

  const getDescriptionOfProjectCategory = (debtClass: DebtClass) => {
    if (debtClass.economicExpert === null || debtClass.legalExpert === null)
      return 'Не указана категория';
    const valueCategory = `${debtClass.economicExpert}${debtClass.legalExpert}`;
    const descriptionOfProjectCategory = valuesOfProjectCategory[valueCategory];
    if (!descriptionOfProjectCategory) return 'Не известная категория';
    return (
      debtDescriptionOfProjectCategory[descriptionOfProjectCategory] ?? 'Нет описания к категории'
    );
  };

  let startDate = null;
  let endDate = null;

  if (project.loanIssuedAt) {
    if (!isAnnounced(project.status) || !isFundRaising(project.status)) {
      startDate = formatDate(project.loanIssuedAt);
    }
  }

  if (project.paidAt) {
    if (!isAnnounced(project.status) || !isFundRaising(project.status)) {
      endDate = formatDate(project.paidAt);
    }
  }

  return (
    <>
      <div className={styles.detailedProjectMain}>
        <div className={styles.detailedProjectMain__top}>
          <div className={styles.detailedProjectMain__status}>
            <div className={styles.detailedProjectMain__code}>{project.paymentCode}</div>
            <div>
              {translateProjectStatus(project.status)}{' '}
              {project.uuid === '617bdbed-1449-4d0d-9d31-2e77b80f967b' && '- Вторичный рынок'}
            </div>
          </div>
          <div className={styles.detailedProjectMain__classContainer}>
            {project.accounting.hasExtraCreditorPayment && (
              <div className={styles.detailedProjectMain__class}>инкассо</div>
            )}
            <div
              className={styles.detailedProjectMain__class}
              onClick={() => ymAsync('commission-size-project-card')}
            >
              <Tooltip
                content={
                  'Размер вознаграждения агента за инвестицию / Размер вознаграждения агента за доход'
                }
                position={'bottom'}
              >
                {`${investmentFeePercent}/${dividendInterestFeePercent}\xa0%`}
              </Tooltip>
            </div>
            <div className={styles.detailedProjectMain__class}>
              <Tooltip
                content={getDescriptionOfProjectCategory(project.debt.debtClass)}
                position={'bottom'}
              >
                <ProjectCategory debtClass={project.debt.debtClass} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.detailedProjectMain__project}>
          <div>
            <p className={styles.detailedProjectMain__name}>{project.name[0].text}</p>
            <p className={styles.detailedProjectMain__info}>
              ИНН {project.company.inn}. {project.company.name}.
            </p>
            <div className={styles.detailedProjectMain__dateContainerLayout}>
              <div className={styles.detailedProjectMain__dateContainer}>
                <p className={styles.detailedProjectMain__dateTitle}>Начало проекта</p>
                <p className={styles.detailedProjectMain__dateValue}>
                  {startDate === null ? '-' : startDate}
                </p>
              </div>
              <div className={styles.detailedProjectMain__dateContainer}>
                <p className={styles.detailedProjectMain__dateTitle}>Конец проекта</p>
                <p className={styles.detailedProjectMain__dateValue}>
                  {endDate === null ? '-' : endDate}
                </p>
              </div>
            </div>
          </div>
          {project.uuid === '617bdbed-1449-4d0d-9d31-2e77b80f967b' && (
            <div>
              <div className={styles.detailedProjectMain__oredrsSum}>
                <div className={styles.detailedProjectMain__oredrsSum__text}>
                  Продажа части права требования по проекту
                </div>
                <Link
                  to="/dashboard/project/3bbc1625-1c74-4040-822b-511f4ee1a3e3"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href =
                      '/dashboard/project/3bbc1625-1c74-4040-822b-511f4ee1a3e3';
                  }}
                >
                  №388: АО &quot;КРАСЛЕСИНВЕСТ&quot;
                </Link>
              </div>
            </div>
          )}
        </div>

        {project.accounting.detailedInfo && (
          <div className={styles.detailedProjectMainIncasso}>
            <div className={styles.detailedProjectMainIncasso__title}>Особенности проекта</div>
            <div
              className={styles.detailedProjectMainIncasso__text}
              dangerouslySetInnerHTML={{ __html: project.accounting.detailedInfo }}
            ></div>
          </div>
        )}
        <div className={styles.detailedProjectMain__actions}>
          <Button
            className={styles.detailedProjectMain__downloadButton}
            onClick={downloadPresentation}
            disabled={presentationLoading}
          >
            {presentationLoading ? 'Формируем PDF…' : 'Скачать PDF о проекте'}
          </Button>
        </div>
      </div>
      <ProjectMainStats />
      <div className={styles.detailedProjectReason}>
        <div className={styles.detailedProjectAdditional}>
          <div className={styles.detailedProjectAdditional__title}>
            Основания предполагать будущее погашение долга
          </div>
          <div className={styles.detailedProjectAdditional__items}>{renderPaymentBases}</div>
        </div>
      </div>
    </>
  );
};

export default ProjectMain;
